import { ClientEvents } from 'common/events/ClientEvents';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import mealPlanShefBag from 'src/assets/img/meal-plans/mealPlanShefBag.jpg';
import curlyUnderline from 'src/assets/svg/meal-plan/curlyUnderline.svg';
import newTag from 'src/assets/svg/meal-plan/newTag.svg';
import weeklyShefService from 'src/assets/svg/meal-plan/weeklyShefService.svg';
import weeklyShefServiceSmall from 'src/assets/svg/meal-plan/weeklyShefServiceSmall.svg';
import { useMealPlanSurfacingLogic } from 'src/pages/consumer/meal-plans/hooks/useMealPlanSurfacingLogic';
import { MEAL_PLANS_INTAKE_FIRST_STEP } from 'src/pages/consumer/meal-plans/meal-plan-intake/preferences/consts';
import { useMealPlanDiscount } from 'src/pages/consumer/meal-plans/useMealPlanDiscount';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';
import { StyledModal } from 'web-common/src/shared/design-system/components/Modal';
import { MEAL_PLAN_VALUE_PROPS } from './consts';
import { EllipsedEdgeColor, EllipsedEdgeContainer } from './EllipsedEdgeContainer';

interface ExploreMealPlansModalProps {
  onClose: () => void;
  isShowing: boolean;
}

const ExploreMealPlansModal = ({ onClose, isShowing }: ExploreMealPlansModalProps) => {
  const history = useHistory();
  const tracker = useTracker();
  const { mealPlanIntakeRouteByRegion } = useMealPlanSurfacingLogic();
  const { promoEndText, totaledDiscountText } = useMealPlanDiscount();
  const { code } = usePromotionalsHook();

  useEffect(() => {
    if (isShowing) {
      tracker.track(ClientEvents.EXPLORE_MEAL_PLAN_MODAL_SHOWN, {});
    }
  }, [isShowing, tracker]);

  const close = () => {
    onClose();
    tracker.track(ClientEvents.EXPLORE_MEAL_PLAN_MODAL_CLOSED, {});
  };

  const accept = () => {
    tracker.track(ClientEvents.EXPLORE_MEAL_PLAN_MODAL_CLAIM_OFFER, {});
    onClose();
    if (mealPlanIntakeRouteByRegion) {
      history.push(mealPlanIntakeRouteByRegion);
    } else {
      history.push(MEAL_PLANS_INTAKE_FIRST_STEP);
    }
  };

  return (
    <StyledModal
      isShowing={isShowing}
      primaryButtonText={code?.isMealPlanEnabled ? 'Claim offer' : 'Get started'}
      showCloseButton={false}
      secondaryButtonText='No thanks'
      removeContainerPadding
      onClose={close}
      onAccept={accept}>
      <div className='w-full overflow-hidden rounded-t-xl'>
        <div className='fixed z-10 w-full'>
          <div className='relative mb-0 w-full bg-mint-400 p-2 text-center text-white'>
            <button className='absolute right-2 top-2 bg-none' onClick={close}>
              <Icon name='close' />
            </button>
            <p className='text-body-700 uppercase'>
              {code?.isMealPlanEnabled ? (
                <span>
                  Claim <span className='font-juniper-bay text-h4-bold'>{totaledDiscountText}</span> {promoEndText}
                </span>
              ) : (
                <p className='m-0 py-1'>Set up your Weekly Shef Service</p>
              )}
            </p>
            {code?.isMealPlanEnabled && <p className='text-label-400'>Discount applied at checkout</p>}
          </div>
        </div>
        <img
          src={mealPlanShefBag}
          alt='Weekly Shef Service'
          className='-mt-px box-border h-[250px] w-full object-cover object-center md:h-[300px]'
        />

        <EllipsedEdgeContainer color={EllipsedEdgeColor.SOURDOUGH}>
          <div className='px-8 pb-8'>
            <div className='mb-2 flex justify-center'>
              <img src={newTag} className='-mt-4 w-14' alt='New!' />
            </div>
            <div className='flex w-full flex-col items-center gap-4 md:flex-row md:gap-2'>
              <div className='flex grow flex-col items-center gap-1 md:max-w-[60%] md:gap-2'>
                <img src={weeklyShefService} alt='Weekly Shef Service' className='w-full max-md:hidden' />
                <img src={weeklyShefServiceSmall} alt='Weekly Shef Service' className='w-[300px] md:hidden' />
                <img src={curlyUnderline} alt='' className='' />
              </div>
              <div className='flex flex-col gap-2'>
                {MEAL_PLAN_VALUE_PROPS.map(({ copy, iconName }) => (
                  <p className='m-0 flex items-center gap-1 text-caption-700 text-eggplant legacy-sm:gap-2' key={copy}>
                    <Icon name={iconName} color={IconColor.RASPBERRY} />
                    {copy}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </EllipsedEdgeContainer>
      </div>
    </StyledModal>
  );
};

export default ExploreMealPlansModal;
